import React, { createContext, useContext, useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { doc, getDoc, getFirestore, onSnapshot } from 'firebase/firestore';
import { getDocument, cloudFunctionV2 } from '../functions';
import { useUser } from './UserContext';

const CompanyContext = createContext(null);

export const useCompany = () => {
    const context = useContext(CompanyContext);
    if (!context) {
        throw new Error('useCompany must be used within CompanyProvider');
    }
    return context;
};

export const CompanyProvider = ({ children }) => {
    const { userData, user, refreshUser } = useUser();
    const [companyData, setCompanyData] = useState(null);
    // `null` while the live listener is still waiting for its first snapshot.
    // `[]` means "loaded, no accounts connected." A non-empty array means at
    // least one Unipile mailbox is wired up.
    const [unipileAccounts, setUnipileAccounts] = useState(null);
    const [loading, setLoading] = useState(true);
    const [companyLoadError, setCompanyLoadError] = useState(false);
    const [customerId, setCustomerId] = useState(null);
    const invitationCheckDone = useRef(false);

    // Handle the no-company states (logged out, or a user with no companyId yet).
    // When a companyId IS present, the live listener below owns companyData,
    // customerId, and loading — this effect deliberately doesn't touch them.
    useEffect(() => {
        if (!userData) {
            setCompanyData(null);
            setCustomerId(null);
            setLoading(false);
            setCompanyLoadError(false);
            invitationCheckDone.current = false;
            return;
        }

        if (userData.companyId && userData.companyId !== "") return;

        // Keep `loading` true until we've decided whether this user has a
        // pending invitation (which can populate companyId asynchronously).
        // Consumers like RequireOnboarding depend on this flag to avoid
        // bouncing an invited user to /onboarding/business before the
        // server-side auto-accept completes.
        setLoading(true);

        const freshSignupOnboarding = typeof window !== 'undefined'
            && window.sessionStorage.getItem('freshSignupOnboarding') === '1'
            && window.location.pathname.startsWith('/onboarding');
        if (freshSignupOnboarding) {
            setCompanyData(null);
            setCustomerId(null);
            setLoading(false);
            return;
        }

        // No company — check for pending invitation (once per session)
        const checkPendingInvitation = async () => {
            if (!invitationCheckDone.current && user?.userId) {
                invitationCheckDone.current = true;
                try {
                    const url = process.env.REACT_APP_CHECK_PENDING_INVITATION;
                    if (url) {
                        const result = await cloudFunctionV2(url, { userId: user.userId });
                        if (result.status === 200 && result.data?.found === true) {
                            // Invitation auto-accepted — refresh user data to pick up new companyId
                            if (refreshUser) {
                                await refreshUser();
                            }
                            // The effect will re-run with updated userData containing companyId
                            return;
                        }
                    }
                } catch (error) {
                    console.error('Error checking pending invitation:', error);
                }
            }
            setCompanyData(null);
            setCustomerId(null);
            setLoading(false);
        };

        checkPendingInvitation();
    }, [userData, user, refreshUser]);

    // Live-subscribe to companies/{companyId}. Server-side writes — notably the
    // Stripe webhook (functions/stripeWebhookLive.js) provisioning credits/plan a
    // few seconds after checkout success — flip the UI without a manual refresh or
    // reload. The company fields live under a top-level `data` map, mirroring
    // getDocument's `queryData.data.data` access (the webhook writes `{ data }`).
    useEffect(() => {
        const companyId = userData?.companyId;
        if (!companyId) return undefined;
        setLoading(true);
        setCompanyLoadError(false);
        const db = getFirestore();
        const ref = doc(db, 'companies', companyId);
        const unsubscribe = onSnapshot(
            ref,
            (snap) => {
                // Set error flag based on doc existence so OnboardingFlow can rescue
                // users whose companyId points to a deleted/missing doc (blank-screen
                // trap), and clear it when the doc exists so transient errors self-heal.
                setCompanyLoadError(!snap.exists());
                const companyDocData = snap.exists() ? (snap.data()?.data ?? null) : null;
                setCompanyData(companyDocData);
                const isAdmin = !!companyDocData?.users?.some(
                    e => e.userId === user?.userId && e.admin === true
                );
                setCustomerId(isAdmin && userData?.customerId ? userData.customerId : null);
                setLoading(false);
            },
            (err) => {
                console.error('Error subscribing to company doc:', err);
                setCompanyLoadError(true);
                setLoading(false);
            }
        );
        return () => unsubscribe();
    }, [userData?.companyId, user?.userId, userData?.customerId]);

    // Live-subscribe to unipile_accounts/{companyId}. The Unipile notify webhook
    // (functions/flows/unipile/unipileNotifyCallback.js) writes here the moment
    // a hosted-auth flow completes, so any open Integrations / Home tab flips
    // to "connected" within ~1s without the user touching anything.
    useEffect(() => {
        const companyId = userData?.companyId;
        if (!companyId) {
            setUnipileAccounts(null);
            return undefined;
        }
        const db = getFirestore();
        const ref = doc(db, 'unipile_accounts', companyId);
        const unsubscribe = onSnapshot(
            ref,
            (snap) => {
                // Webhook writes `{ accounts: [...] }` directly — no `.data` envelope.
                const accounts = (snap.exists() && Array.isArray(snap.data()?.accounts))
                    ? snap.data().accounts
                    : [];
                setUnipileAccounts(accounts);
            },
            (err) => {
                console.error('Error subscribing to unipile_accounts:', err);
                // Treat a failed subscription as "no accounts" rather than
                // leaving the UI stuck in a loading state forever.
                setUnipileAccounts([]);
            }
        );
        return () => unsubscribe();
    }, [userData?.companyId]);

    // When the tab regains focus after being backgrounded, Chrome may have
    // throttled the onSnapshot WebSocket callback for up to 60s. A single
    // getDoc on visibility restore unblocks the UI immediately. (The Unipile
    // hosted-auth flow is now a full-page redirect so this path is rarely the
    // hot one for it, but the visibility safety net still helps any user who
    // tabs away while the listener is rehydrating.)
    useEffect(() => {
        const companyId = userData?.companyId;
        if (!companyId) return undefined;
        let ignore = false;
        const db = getFirestore();
        const ref = doc(db, 'unipile_accounts', companyId);
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                getDoc(ref).then(snap => {
                    if (ignore) return;
                    const accounts = (snap.exists() && Array.isArray(snap.data()?.accounts))
                        ? snap.data().accounts
                        : [];
                    setUnipileAccounts(accounts);
                }).catch(() => {});
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            ignore = true;
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [userData?.companyId]);

    const hasSmsAllowlist = Array.isArray(companyData?.smsConfig?.allowedFromNumbers)
        && companyData.smsConfig.allowedFromNumbers.length > 0;
    const hasUserPhone = !!(userData?.phoneNumber);
    const hasUnipile = Array.isArray(unipileAccounts) && unipileAccounts.length > 0;
    const integrationsConnected = hasUnipile || hasSmsAllowlist || hasUserPhone;

    const refreshCompany = useCallback(async () => {
        // Unipile accounts now flow through the live snapshot listener above, so
        // refreshCompany only needs to re-pull the (rarely-changing) company doc.
        if (userData?.companyId) {
            const queryData = await getDocument("companies", userData.companyId, true);
            if (queryData.status === 200) {
                setCompanyData(queryData.data.data);
                setCompanyLoadError(false);
            }
        }
    }, [userData?.companyId]);

    const hydrateCompanyData = useCallback((nextCompanyData) => {
        if (!nextCompanyData || typeof nextCompanyData !== 'object') return;
        setCompanyData(nextCompanyData);
        setCompanyLoadError(false);
        const isAdmin = !!nextCompanyData?.users?.some(
            e => e.userId === user?.userId && e.admin === true
        );
        setCustomerId(isAdmin && userData?.customerId ? userData.customerId : null);
        setLoading(false);
        setUnipileAccounts(prev => Array.isArray(prev) ? prev : []);
    }, [user?.userId, userData?.customerId]);

    // Memoize context value to prevent unnecessary re-renders
    const value = useMemo(() => ({
        companyData,
        companyId: userData?.companyId || null,
        companyName: companyData?.companyName || "",
        companySlug: companyData?.companySlug || null,
        companyLogo: companyData?.logo || null,
        ingestionFilter: companyData?.ingestionFilter || { enabled: false, includeMLS: true },
        credits: companyData?.credits ?? 0,
        customerId,
        integrationsConnected,
        unipileAccounts,
        unipileAccountsLoading: unipileAccounts === null,
        loading,
        companyLoadError,
        refreshCompany,
        hydrateCompanyData
    }), [companyData, userData?.companyId, customerId, integrationsConnected, unipileAccounts, loading, companyLoadError, refreshCompany, hydrateCompanyData]);

    return (
        <CompanyContext.Provider value={value}>
            {children}
        </CompanyContext.Provider>
    );
};
